import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { explain } from '@/shared/api/problem'
import { ErrorStrip, LoadingBlock } from '@/shared/ui/states'
import {
  EmptyRow, ErpTable, KpiStrip, MetricCard, Panel, SectionHeader, Td, Th,
} from '@/components/erp/primitives'
import { ScatterQuadrant, type QuadrantPoint } from '@/shared/ui/charts'
import {
  ClearanceBadge, CollectedAt, MomentumBadge, SideBadge, fmtKES, label,
} from '@/features/dossier/components/primitives'
import { getDashboard, type Dashboard } from './api'

/**
 * The Threat Matrix — the landing page.
 *
 * ## The chart is the navigation
 *
 * Each bubble opens a target. That is why `ScatterQuadrant` is keyboard-navigable: a chart
 * that is the only route into a screen and answers only to a mouse excludes anybody using a
 * keyboard from the feature.
 *
 * ## Unscored targets are counted in the KPI strip
 *
 * `unscoredTargets` is displayed as a first-class number rather than hidden, because a
 * matrix that silently dropped the candidates nobody has polled would read as the complete
 * field — and those are exactly the ones an analyst needs to notice. A null score is "not
 * measured", never zero.
 */
export function ThreatMatrixPage() {
  const navigate = useNavigate()
  const [data, setData] = useState<Dashboard | null>(null)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    try {
      setError(null)
      setData(await getDashboard())
    } catch (err) {
      setError(explain(err))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  if (error && !data) return <ErrorStrip message={error} />
  if (!data) return <LoadingBlock label="Building the matrix" />

  const points: QuadrantPoint[] = data.matrix.map((p) => ({
    id: p.targetId,
    label: p.name,
    x: p.electability,
    y: p.vulnerability,
    size: p.threat ?? 40,
    tone: p.threat == null ? 'muted'
      : p.threat >= 75 ? 'danger'
      : p.threat >= 50 ? 'warn' : 'ok',
  }))

  return (
    <div className="space-y-3">
      <SectionHeader
        title="Threat Matrix"
        description="Electability against documented vulnerability. Every score is an
                     assessment on the subject's own file, with its reasoning."
      />

      <KpiStrip>
        <MetricCard label="Targets" value={data.targetCount} />
        <MetricCard
          label="Not scored"
          value={data.unscoredTargets}
          sub="No publishable polling estimate — shown, never dropped"
        />
        <MetricCard label="Evidenced surfaces" value={data.surfacesEvidenced} />
        <MetricCard label="Plays awaiting review" value={data.playsAwaitingReview} />
        <MetricCard label="Active operations" value={data.activeOperations} />
      </KpiStrip>

      <Panel>
        <div className="p-3">
          <ScatterQuadrant
            points={points}
            xLabel="Electability"
            yLabel="Vulnerability"
            onSelect={(id) => navigate(`/opposition/targets/${id}`)}
          />

          {/* WHERE THE AXES COME FROM. An empty matrix is a fair state — most campaigns start
              there — but "empty" and "broken" look identical unless the screen says which
              inputs are missing and where they come from. */}
          {data.unscoredTargets > 0 && (
            <div className="mt-3 rounded border border-dashed border-border p-2">
              <p className="text-[11px] font-medium">What puts a target on the plane</p>
              <ul className="mt-1 list-disc space-y-0.5 pl-5 text-[11px] text-muted-foreground">
                <li>
                  <b>Electability</b> comes from a published survey estimate for that
                  candidate. With no measured share the score is <b>null and stays null</b> —
                  it is never defaulted to zero, because an unmeasured candidate ranked at
                  zero would sort below every measured one. Run a survey in{' '}
                  <Link to="/polling" className="text-primary underline">Polling Studio</Link>,
                  and the score follows.
                </li>
                <li>
                  <b>Vulnerability</b> comes from the exposures recorded against them and how
                  well each is evidenced — record them on the target's own page, under Attack
                  surfaces.
                </li>
                <li>
                  A point needs <b>both</b>. A target with a vulnerability score and no
                  electability estimate cannot be placed, so it waits in the Not scored lane
                  with its name against it.
                </li>
              </ul>
            </div>
          )}
        </div>
      </Panel>

      <Panel>
        <ErpTable head={<>
          <Th>Target</Th>
          <Th>Side</Th>
          <Th className="axis-hide-sm">Stage</Th>
          <Th className="text-right">Threat</Th>
          <Th className="text-right axis-hide-sm">Electability</Th>
          <Th className="text-right axis-hide-sm">Vulnerability</Th>
          <Th>Momentum</Th>
          <Th className="axis-hide-sm">War chest</Th>
          <Th>Scored</Th>
        </>}>
          {data.topThreats.length === 0 && (
            <EmptyRow colSpan={9}
                      message="No targets yet. Open one from a subject's file." />
          )}
          {data.topThreats.map((t) => (
            <tr key={t.id} className="cursor-pointer hover:bg-muted/40"
                onClick={() => navigate(`/opposition/targets/${t.id}`)}>
              <Td>
                <span className="font-medium">{t.displayName}</span>
                <ClearanceBadge clearance={t.clearanceCode} />
              </Td>
              <Td><SideBadge side={t.sideCode} /></Td>
              <Td className="axis-hide-sm">
                <span className="text-[11px] text-muted-foreground">
                  {label(t.killChainStageCode)}
                </span>
              </Td>
              <Td className="text-right tabular-nums">
                {/* "not scored", never 0 — a candidate nobody polled must not rank last. */}
                {t.threatScore == null
                  ? <span className="text-[11px] text-muted-foreground">not scored</span>
                  : Math.round(t.threatScore)}
              </Td>
              <Td className="text-right tabular-nums axis-hide-sm">
                {t.electabilityScore == null ? '—' : Math.round(t.electabilityScore)}
              </Td>
              <Td className="text-right tabular-nums axis-hide-sm">
                {t.vulnerabilityScore == null ? '—' : Math.round(t.vulnerabilityScore)}
              </Td>
              <Td><MomentumBadge momentum={t.momentumCode} /></Td>
              <Td className="axis-hide-sm">{fmtKES(t.warChestKes)}</Td>
              {/* The cache's age, always beside the number it qualifies. */}
              <Td><CollectedAt at={t.scoresAsOf} /></Td>
            </tr>
          ))}
        </ErpTable>
      </Panel>
    </div>
  )
}
